import { ChevronLeft, ChevronRight, Plus, Search, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ConfirmDialog } from '@/components/confirm-dialog';
import { ExportButtons } from '@/components/export-buttons';
import { ResponsiveTable } from '@/components/responsive-table';
import { ListPagination, ScrollArea } from '@/components/scroll-area';
import { SubscriberDialog, type SubscriberValues } from '@/components/subscriber-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSubscribers } from '@/lib/api-hooks';
import { type ExportFormat, downloadExport } from '@/lib/export';
import { invalidateResource } from '@/lib/resource';
import {
  SUBSCRIBER_PAGE_SIZE_OPTIONS,
  type Subscriber,
  type SubscriberSource,
  createSubscriber,
  deleteSubscriber,
} from '@/lib/subscribers';
import { useSubscribersStore } from '@/lib/subscribers-store';
import { cn } from '@/lib/utils';

const sourceStyles: Record<SubscriberSource, string> = {
  jury: 'border-transparent bg-blue-500/15 text-blue-700 dark:text-blue-400',
  post_vote: 'border-transparent bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
};

const sourceFilters: Array<'all' | SubscriberSource> = ['all', 'jury', 'post_vote'];

export function SubscribersPage() {
  const search = useSubscribersStore((state) => state.search);
  const source = useSubscribersStore((state) => state.source);
  const page = useSubscribersStore((state) => state.page);
  const pageSize = useSubscribersStore((state) => state.pageSize);
  const setSearch = useSubscribersStore((state) => state.setSearch);
  const setSource = useSubscribersStore((state) => state.setSource);
  const setPage = useSubscribersStore((state) => state.setPage);
  const setPageSize = useSubscribersStore((state) => state.setPageSize);

  const { data, isLoading } = useSubscribers({ search, source, page, pageSize });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<Subscriber | null>(null);

  async function handleAdd(values: SubscriberValues) {
    await createSubscriber({
      email: values.email,
      name: values.name || undefined,
      source: values.source,
      project: values.source === 'post_vote' && values.project ? values.project : undefined,
    });
    invalidateResource('subscriber');
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  const activeFilters = [
    ...(source === 'all' ? [] : [`source: ${source.replace('_', ' ')}`]),
    ...(search.trim() ? [`search: "${search.trim()}"`] : []),
  ];

  async function handleExport(format: ExportFormat) {
    try {
      await downloadExport(
        'subscribers',
        { source: source === 'all' ? undefined : source, search },
        'subscribers',
        format,
      );
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full sm:w-60">
          <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search subscribers"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-8"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus />
            Add subscriber
          </Button>
          <ExportButtons onExport={handleExport} filters={activeFilters} />
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        {sourceFilters.map((filter) => (
          <Button
            key={filter}
            size="sm"
            variant={source === filter ? 'default' : 'ghost'}
            onClick={() => setSource(filter)}
            className="h-8 capitalize"
          >
            {filter === 'all' ? 'All' : filter.replace('_', ' ')}
          </Button>
        ))}
      </div>

      <ScrollArea>
        <ResponsiveTable
          loading={isLoading}
          loadingLabel="Loading subscribers…"
          skeletonRows={pageSize}
          emptyLabel="No subscribers match your filters."
          rows={items}
          rowKey={(subscriber) => subscriber.id}
          columns={[
            {
              key: 'email',
              header: 'Email',
              title: true,
              cell: (subscriber: Subscriber) => (
                <span className="font-medium">{subscriber.email}</span>
              ),
            },
            {
              key: 'name',
              header: 'Name',
              secondary: true,
              cell: (subscriber: Subscriber) => (
                <span className="text-muted-foreground">{subscriber.name ?? '—'}</span>
              ),
            },
            {
              key: 'source',
              header: 'Source',
              cell: (subscriber: Subscriber) => (
                <Badge variant="outline" className={cn(sourceStyles[subscriber.source])}>
                  {subscriber.source.replace('_', ' ')}
                </Badge>
              ),
            },
            {
              key: 'project',
              header: 'Project',
              secondary: true,
              cell: (subscriber: Subscriber) =>
                subscriber.projectSlug && subscriber.project ? (
                  <Link
                    to={`/projects/${subscriber.projectSlug}`}
                    className="text-muted-foreground hover:text-foreground hover:underline"
                  >
                    {subscriber.project}
                  </Link>
                ) : (
                  '—'
                ),
            },
            {
              key: 'date',
              header: 'Date',
              align: 'right',
              cell: (subscriber: Subscriber) => (
                <span className="text-muted-foreground">{subscriber.date}</span>
              ),
            },
          ]}
          actions={(subscriber) => (
            <Button
              variant="ghost"
              size="icon"
              className="size-8 text-destructive hover:text-destructive"
              onClick={() => setDeleting(subscriber)}
            >
              <Trash2 className="size-4" />
              <span className="sr-only">Delete {subscriber.email}</span>
            </Button>
          )}
        />
        {(total > 0 || isLoading) && (
          <ListPagination>
            <p className="text-muted-foreground text-sm">
              {isLoading ? 'Loading…' : `Showing ${start}–${end} of ${total}`}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">Rows per page</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => setPageSize(Number(value))}
                >
                  <SelectTrigger size="sm" className="w-[4.5rem]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUBSCRIBER_PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1 || isLoading}
                >
                  <ChevronLeft />
                  Previous
                </Button>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((number) => (
                  <Button
                    key={number}
                    variant={number === page ? 'default' : 'ghost'}
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPage(number)}
                    disabled={isLoading}
                  >
                    {number}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages || isLoading}
                >
                  Next
                  <ChevronRight />
                </Button>
              </div>
            </div>
          </ListPagination>
        )}
      </ScrollArea>

      {deleting && (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) setDeleting(null);
          }}
          title="Remove subscriber?"
          description={`${deleting.email} will be removed from the mailing list.`}
          confirmLabel="Remove"
          onConfirm={async () => {
            await deleteSubscriber(deleting.id);
            invalidateResource('subscriber');
          }}
        />
      )}

      <SubscriberDialog open={adding} onOpenChange={setAdding} onSubmit={handleAdd} />
    </div>
  );
}
